import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import "../css/coverpage.css";
import gettingStartedPlaylist from "../img/getting-started-playlist.jpg";
import swifthelpbirdwhite from "../img/swifthelpbirdwhite.svg";

const PLAYLIST_URL =
  "https://www.youtube.com/watch?v=mgqROGJOp1k&list=PL5MJvbOcQoX8F_IKqgCTSez2njsfL175l";

const PlayIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M21.6 7.2a2.8 2.8 0 0 0-2-2C17.9 4.8 12 4.8 12 4.8s-5.9 0-7.6.4a2.8 2.8 0 0 0-2 2C2 8.9 2 12 2 12s0 3.1.4 4.8a2.8 2.8 0 0 0 2 2c1.7.4 7.6.4 7.6.4s5.9 0 7.6-.4a2.8 2.8 0 0 0 2-2c.4-1.7.4-4.8.4-4.8s0-3.1-.4-4.8ZM10 15.2V8.8l5.2 3.2L10 15.2Z" />
  </svg>
);

function CoverPage() {
  return (
    <div className="coverpage">
      <Helmet>
        <title>SwiftGrade Help Center</title>
      </Helmet>

      <header>
        <div className="bar">
          <div className="brand">
            <img src={swifthelpbirdwhite} alt="SwiftGrade" />
            SwiftGrade <span className="tag">Help</span>
          </div>
          <a
            className="go"
            href="https://app.goswiftgrade.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Go to SwiftGrade ↗
          </a>
        </div>
      </header>

      <section className="hero">
        <div className="hero-inner">
          <h1>Welcome to SwiftGrade 2.0</h1>
          <p>
            Take the tests, essays or projects sitting on your desk and
            upload them exactly as-is — our AI grades them with full
            feedback on every question. Our original answer sheets still
            work too.
          </p>
          <div className="cta-row">
            <a
              className="btn btn-primary"
              href={PLAYLIST_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <PlayIcon />
              Watch the Getting Started videos
            </a>
            <a className="btn btn-ghost" href="#questions">
              Common questions
            </a>
          </div>
        </div>
      </section>

      <main>
        <div className="panel">
          <a
            className="feature"
            href={PLAYLIST_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img
              className="thumb"
              alt="SwiftGrade tutorials — everything in one place"
              src={gettingStartedPlaylist}
            />
            <div>
              <h2>Getting Started playlist</h2>
              <p>
                Seven short videos that walk you through grading your first
                assessment in V2 — from answer key or rubric to sending
                results.
              </p>
            </div>
            <svg
              className="arrow"
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </a>
        </div>

        <div className="sec-head" id="questions">
          <h2>Common questions</h2>
          <p>
            A full help center for V2 is on the way. In the meantime, here
            are the things teachers ask most.
          </p>
        </div>

        <div className="faq">
          <details open>
            <summary>
              What can SwiftGrade grade? <span className="plus">+</span>
            </summary>
            <div className="ans">
              <p>
                Almost anything — any question type, any subject, including
                handwritten work (that's our specialty). It doesn't just
                mark right or wrong: it reads each student's actual work —
                steps, process, open-ended answers — and gives feedback on
                it.
              </p>
              <p>Grading falls into two categories:</p>
              <ol>
                <li>
                  <strong>Tests &amp; quizzes</strong> — upload a photo or
                  PDF of your answer key, and our AI grades each question.
                </li>
                <li>
                  <strong>Essays &amp; projects</strong> — upload a photo or
                  PDF of your rubric, and our AI grades the whole assessment
                  against it.
                </li>
              </ol>
            </div>
          </details>

          <details>
            <summary>
              How do I get started? <span className="plus">+</span>
            </summary>
            <div className="ans">
              <p>
                Scan your stack of tests into one PDF. Upload it into
                SwiftGrade with your answer key or rubric, and grading
                starts.
              </p>
              <p>
                No tests on hand right now? Try it with our{" "}
                <a
                  href="https://drive.google.com/drive/folders/1HkdrxRIJBZ9lauLccMsHX3v8_kcIJolw?usp=sharing"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  pre-scanned sample tests and essays
                </a>{" "}
                — download, upload, done.
              </p>
            </div>
          </details>

          <details>
            <summary>
              How do I turn paper tests into a PDF?{" "}
              <span className="plus">+</span>
            </summary>
            <div className="ans">
              <p>
                Any way you like. A school photocopier is fastest — scan the
                whole stack into one PDF. No photocopier? Any free phone
                scanning app works: Genius Scan, Adobe Scan, Microsoft Lens.
              </p>
            </div>
          </details>

          <details>
            <summary>
              Do I need to create my test inside SwiftGrade?{" "}
              <span className="plus">+</span>
            </summary>
            <div className="ans">
              <p>
                No. SwiftGrade is grading software, not test-creation
                software. Nothing to set up — just upload completed student
                work plus your answer key or rubric.
              </p>
            </div>
          </details>

          <details>
            <summary>
              How accurate is the grading? <span className="plus">+</span>
            </summary>
            <div className="ans">
              <p>
                Remarkably accurate on most question types — it can
                occasionally struggle with highly visual ones, like a
                hand-drawn graph or diagram, but that's improving all the
                time. SwiftGrade is built on the world's most advanced AI
                models (the same family that powers ChatGPT, Gemini and
                Claude), so it gets better as they do.
              </p>
              <p>
                It's designed as a solid first pass: the AI does the heavy
                lifting, then our review tools let you check and adjust
                quickly — the final marks are always yours.
              </p>
            </div>
          </details>

          <details>
            <summary>
              Do students like it? <span className="plus">+</span>
            </summary>
            <div className="ans">
              <p>
                Yes — students get detailed feedback on every question while
                the test is still fresh in their mind, not a week later.
                Parents love it too: you can send the full feedback straight
                home, instantly. And you stay in control of what gets sent
                and when.
              </p>
            </div>
          </details>

          <details>
            <summary>
              Do I need to add my class roster to start grading?{" "}
              <span className="plus">+</span>
            </summary>
            <div className="ans">
              <p>
                No — you can start grading without it. But we do recommend
                it, because it takes a minute and everything gets easier:
              </p>
              <ul>
                <li>
                  Uploaded tests auto-match to your roster — no fixing names
                  the AI misread
                </li>
                <li>
                  Send results in one click to each student's portal, or
                  their email
                </li>
                <li>Add a parent email and send results straight home</li>
                <li>
                  On paper answer sheets, names come pre-printed for perfect
                  matching
                </li>
              </ul>
            </div>
          </details>

          <details>
            <summary>
              How much does it cost? <span className="plus">+</span>
            </summary>
            <div className="ans">
              <p>
                SwiftGrade is currently free to use. For school or district
                licensing, email{" "}
                <a href="mailto:hello@goswiftgrade.com">
                  hello@goswiftgrade.com
                </a>
                .
              </p>
            </div>
          </details>

          <details>
            <summary>
              Is there a mobile app? <span className="plus">+</span>
            </summary>
            <div className="ans">
              <p>
                Yes — iOS and Android (for teachers, not students). The app
                is used for our original answer sheets (scanning paper
                bubble sheets and written answer sheets). V2 uploads are
                done on the website.
              </p>
            </div>
          </details>

          <details>
            <summary>
              Is there a student portal? <span className="plus">+</span>
            </summary>
            <div className="ans">
              <p>
                Yes — students can see all their results there, and also
                take our online answer sheets from it.
              </p>
            </div>
          </details>
        </div>

        <div className="classic">
          <div className="txt">
            <h3>Using our original answer sheets?</h3>
            <p>
              If you're working with our paper or online answer sheets, the
              V1 help center is fully available.
            </p>
          </div>
          <Link className="link" to="/classic">
            Open the classic help center →
          </Link>
        </div>
      </main>

      <footer>
        Need a hand? Email{" "}
        <a href="mailto:hello@goswiftgrade.com">hello@goswiftgrade.com</a>
        &nbsp;·&nbsp; SwiftGrade is a product of Educora Inc.
      </footer>
    </div>
  );
}

export default CoverPage;
